import { useCallback, useRef } from "react";
import { v4 } from "uuid";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { type ClaimsEvidenceReasoningFormData } from "@/types/models/Exportables.interface";
import { AdjustFontSizeToFitContent } from "@/view/utils/useAdjustFontSizeToFitContent";
import * as styles from "./ClaimsEvidenceReasoning.styles";
import { createFieldsData, FieldProps, isContextCluesFormData } from "./utils";

const CUSTOM_INPUT_MAX_LENGTH = 220;

const ClaimsEvidenceReasoning = ({
  formData,
  onChange,
}: TemplateProps<ClaimsEvidenceReasoningFormData>) => {
  const uniqueId = useRef(v4()).current;
  const cerData = isContextCluesFormData(formData)
    ? formData
    : ({} as ClaimsEvidenceReasoningFormData);

  const fieldsData = createFieldsData();

  const renderField = useCallback(
    (fields: FieldProps[]) => {
      return (
        <div css={styles.fieldStyles}>
          {fields.map(({ label, field }) => {
            const textareaId = `cer-${field}-input-${uniqueId}`;
            const trimmedValue = `${cerData[field] || ""}`.substring(
              0,
              CUSTOM_INPUT_MAX_LENGTH,
            );

            return (
              <div key={textareaId} css={styles.fieldContentWrapperStyles}>
                <p css={styles.fieldLabelStyles}>{label}</p>
                <AdjustFontSizeToFitContent deps={[trimmedValue]}>
                  <textarea
                    id={textareaId}
                    data-qa-selector={textareaId}
                    css={styles.textareaStyles}
                    value={trimmedValue}
                    onChange={(e) =>
                      onChange?.({ ...cerData, [field]: e.target.value })
                    }
                    maxLength={CUSTOM_INPUT_MAX_LENGTH}
                  />
                </AdjustFontSizeToFitContent>
              </div>
            );
          })}
        </div>
      );
    },
    [cerData, uniqueId, onChange],
  );

  return (
    <div css={styles.containerStyles}>
      <ExportablesTemplateHeaderSection
        templateTitle="Claim-Evidence-Reasoning"
        articleTitle={cerData.article_title}
      />

      <div css={styles.contentStyles}>{fieldsData.map(renderField)}</div>
    </div>
  );
};

export default ClaimsEvidenceReasoning;
